import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Modal, Spinner, Badge, Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import { clearAccountCache } from '../shared/accountCache';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import { formatDateWithLocale } from '../shared/utils';

export default function Settings() {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);  
  const [accountTier, setAccountTier] = useState('free');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [useGooglePhotos, setUseGooglePhotos] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [loadingTokenUsage, setLoadingTokenUsage] = useState(false);
  const [imagesList, setImagesList] = useState([]);
  const [savingImages, setSavingImages] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist
  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch user data
      const response = await apiCall(
        `/api/get?action=${ApiActions.GET_USER_DATA}`,
        {},
        'GET'
      );
      if (response.success) {
        setUserData(response.user);
      } else {
        setError(response.message || t('settings.failedToLoadUserData'));
      }
      
      // Fetch account data to check if admin
      const accountResponse = await apiCall(
        `/api/get?action=${ApiActions.GET_ACCOUNT_DATA}`,
        {},
        'GET'
      );
      if (accountResponse.success && accountResponse.account) {
        setAccountData(accountResponse.account);
        setAccountTier(accountResponse.account.subscription?.tier || 'free');
        setUseGooglePhotos(accountResponse.account.settings?.useGooglePhotos || false);
        setImagesList(accountResponse.account.settings?.images || []);
      }
      
      // Fetch token usage
      await fetchTokenUsage();
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(t('settings.errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenUsage = async () => {
    setLoadingTokenUsage(true);
    try {
      const response = await apiCall(
        `/api/subscription?action=${ApiActions.GET_TOKEN_USAGE}`,
        {},
        'POST'
      );
      if (response.success && response.usage) {
        setTokenUsage(response.usage);
      }
    } catch (err) {
      console.error('Error fetching token usage:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingTokenUsage(false);
    }
  };

  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError(t('settings.pleaseTypeDelete'));
      return;
    }

    setIsDeleting(true);
    setError('');
    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.DELETE_ACCOUNT}`,
        {},
        'POST'
      );
      if (response.success) {
        // Logout and redirect to home
        logout();
        navigate('/', { state: { message: t('settings.accountDeletedSuccess') } });
      } else {
        setError(response.message || t('settings.failedToDeleteAccount'));
        setIsDeleting(false);
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(t('settings.errorDeletingAccount'));
      setIsDeleting(false);
    }
  };

  const handleOpenCancelModal = () => {
    // Calculate subscription end date (cancel at period end - no refunds)
    const now = new Date();
    const subscriptionStart = accountData.subscription?.createdAt ? new Date(accountData.subscription.createdAt) : new Date();
    const billingInterval = accountData.subscription?.planInterval || 'month';
    const daysInPeriod = billingInterval === 'year' ? 365 : 31;
    const daysSinceStart = Math.floor((now - subscriptionStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, daysInPeriod - daysSinceStart);
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + daysRemaining);
    setSubscriptionEndDate(endDate);
    setShowCancelModal(true);
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setError('');

    try {
      const response = await apiCall(
        `/api/subscription?action=${ApiActions.CANCEL_SUBSCRIPTION}`,
        {},
        'POST'
      );

      if (response.success) {
        // Use messageCode with translation if available, otherwise use message or fallback
        let successMessage;
        if (response.messageCode) {
          // Format the date for the translation
          const formattedDate = response.endsAt ? formatDateWithLocale(new Date(response.endsAt), 'PPP') : '';
          successMessage = t(response.messageCode, { date: formattedDate });
        } else {
          successMessage = response.message || t('settings.subscriptionCancelledSuccess');
        }
        setSuccess(successMessage);
        setShowCancelModal(false);
        // Refresh account data
        await fetchUserData();
      } else {
        setError(response.message || t('settings.failedToCancelSubscription'));
        setIsCancelling(false);
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(t('settings.errorCancellingSubscription'));
      setIsCancelling(false);
    }
  };

  const handleAddImageLink = () => {
    setImagesList(prev => [...prev, { url: '', name: '', type: 'icon' }]);
  };

  const handleRemoveImageLink = (index) => {
    setImagesList(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateImageLink = (index, field, value) => {
    setImagesList(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSaveImages = async () => {
    setSavingImages(true);
    setError('');
    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.UPDATE_ACCOUNT_SETTINGS}`,
        {
          settings: {
            ...(accountData?.settings || {}),
            images: imagesList
          }
        },
        'POST'
      );
      if (response.success) {
        setSuccess(t('settings.imagesSaved'));
        clearAccountCache();
        setAccountData(prev => ({
          ...prev,
          settings: {
            ...(prev?.settings || {}),
            images: response.settings?.images ?? imagesList
          }
        }));
      } else {
        setError(response.message || t('settings.failedToSavePreference'));
      }
    } catch (err) {
      console.error('Error saving images:', err);
      setError(t('settings.errorSavingPreference'));
    } finally {
      setSavingImages(false);
    }
  };

  const handleGooglePhotosToggle = async (checked) => {
    setUseGooglePhotos(checked);
    setSavingPreference(true);
    setError('');

    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.UPDATE_ACCOUNT_SETTINGS}`,
        {
          settings: {
            useGooglePhotos: checked
          }
        },
        'POST'
      );

      if (response.success) {
        setSuccess(t('settings.googlePhotosSaved'));
        // Update accountData to reflect the change
        setAccountData(prev => ({
          ...prev,
          settings: {
            ...(prev?.settings || {}),
            useGooglePhotos: checked
          }
        }));
      } else {
        setError(response.message || t('settings.failedToSavePreference'));
        // Revert the checkbox state
        setUseGooglePhotos(!checked);
      }
    } catch (err) {
      console.error('Error updating Google Photos preference:', err);
      setError(t('settings.errorSavingPreference'));
      // Revert the checkbox state
      setUseGooglePhotos(!checked);
    } finally {
      setSavingPreference(false);
    }
  };

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist

  return (
    <>
      <SlidingMenu title={t('settings.title')} />
      <Container className="py-4">
        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('settings.loadingSettings')}</p>
          </div>
        ) : (
          <Row>
            <Col lg={6} className="mb-4">
              <Card className="shadow-sm h-100">
                <Card.Body className="p-4">
                  <h4 className="mb-4">{t('settings.accountInformation')}</h4>
                  
                  <div className="mb-4">
                    <label className="form-label fw-bold">{t('settings.emailAddress')}</label>
                    <div className="form-control-plaintext">
                      {userData?.email || currentUser?.email || 'N/A'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">{t('settings.name')}</label>
                    <div className="form-control-plaintext">
                      {userData?.name || currentUser?.name || 'N/A'}
                    </div>
                  </div>

                  {userData?.createdAt && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">{t('settings.memberSince')}</label>
                      <div className="form-control-plaintext">
                        {formatDateWithLocale(userData.createdAt, 'PPP')}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="form-label fw-bold">{t('settings.accountType')}</label>
                    <div className="form-control-plaintext">
                      {userData?.authProvider === 'google' ? (
                        <span>
                          <i className="bi bi-google text-danger me-2"></i>
                          {t('settings.connectedWithGoogle')}
                          <Badge bg="success" className="ms-2">{t('settings.verified')}</Badge>
                        </span>
                      ) : (
                        <span>
                          <i className="bi bi-envelope-fill text-primary me-2"></i>
                          {t('settings.emailPassword')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="useGooglePhotos"
                      label={
                        <span>
                          <i className="bi bi-images me-2"></i>
                          {t('settings.useGooglePhotos')}
                        </span>
                      }
                      checked={useGooglePhotos}
                      onChange={(e) => handleGooglePhotosToggle(e.target.checked)}
                      disabled={savingPreference}
                    />
                    <div className="form-text mt-2">
                      {t('settings.useGooglePhotosHelp')}
                    </div>
                  </div>

                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="shadow-sm h-100">
                <Card.Body className="p-4">
                  <h4 className="mb-4">{t('settings.subscription')}</h4>
                  
                  <div className="mb-4">
                    <label className="form-label fw-bold">{t('settings.currentPlan')}</label>
                    <div className="form-control-plaintext">
                      {accountTier === 'admin' ? (
                        <Badge bg="danger" className="px-3 py-2">{t('settings.admin')}</Badge>
                      ) : accountTier === 'proplus' ? (
                        <Badge bg="warning" text="dark" className="px-3 py-2">{t('settings.proplus')}</Badge>
                      ) : accountTier === 'pro' ? (
                        <Badge bg="success" className="px-3 py-2">{t('settings.pro')}</Badge>
                      ) : (
                        <Badge bg="secondary" className="px-3 py-2">{t('settings.free')}</Badge>
                      )}
                    </div>
                  </div>

                  {(accountTier === 'pro' || accountTier === 'proplus') && accountData?.subscription && (
                    <>
                      {accountData.subscription.cancelAtPeriodEnd && accountData.subscription.endsAt && (
                        <Alert variant="warning" className="mb-4">
                          <Alert.Heading>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {t('settings.subscriptionCancelled')}
                          </Alert.Heading>
                          <p className="mb-0">
                            {t('settings.subscriptionCancelledText')}{' '}
                            <strong>
                              {formatDateWithLocale(accountData.subscription.endsAt, 'PPP')}
                            </strong>
                            {t('settings.subscriptionCancelledTextEnd')}
                          </p>
                        </Alert>
                      )}

                      {accountData.subscription.createdAt && (
                        <div className="mb-3">
                          <label className="form-label fw-bold">{t('settings.subscriptionStart')}</label>
                          <div className="form-control-plaintext">
                            {formatDateWithLocale(accountData.subscription.createdAt, 'PPP')}
                          </div>
                        </div>
                      )}

                      {accountData.subscription.planInterval && (
                        <div className="mb-3">
                          <label className="form-label fw-bold">{t('settings.billingCycle')}</label>
                          <div className="form-control-plaintext">
                            <i className={`bi bi-${accountData.subscription.planInterval === 'year' ? 'calendar-year' : 'calendar-month'} me-2`}></i>
                            {accountData.subscription.planInterval === 'year' ? t('settings.yearly') : t('settings.monthly')}
                          </div>
                        </div>
                      )}

                      {(() => {
                        // Calculate next billing date
                        if (accountData.subscription.cancelAtPeriodEnd && accountData.subscription.endsAt) {
                          return (
                            <div className="mb-3">
                              <label className="form-label fw-bold">{t('settings.subscriptionEnds')}</label>
                              <div className="form-control-plaintext text-warning">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {formatDateWithLocale(accountData.subscription.endsAt, 'PPP')}
                                <small className="text-muted ms-2">{t('settings.cancelled')}</small>
                              </div>
                            </div>
                          );
                        } else if (accountData.subscription.createdAt && accountData.subscription.planInterval) {
                          const startDate = new Date(accountData.subscription.createdAt);
                          const now = new Date();
                          const interval = accountData.subscription.planInterval === 'year' ? 365 : 31;
                          
                          // Calculate next billing date
                          let nextBilling = new Date(startDate);
                          while (nextBilling <= now) {
                            nextBilling.setDate(nextBilling.getDate() + interval);
                          }
                          
                          return (
                            <div className="mb-3">
                              <label className="form-label fw-bold">{t('settings.nextBillingDate')}</label>
                              <div className="form-control-plaintext">
                                <i className="bi bi-calendar-check me-2"></i>
                                {formatDateWithLocale(nextBilling, 'PPP')}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {!accountData.subscription.cancelAtPeriodEnd && (
                        <div className="mb-4">
                          <Button
                            variant="outline-danger"
                            onClick={handleOpenCancelModal}
                          >
                            <i className="bi bi-x-circle me-2"></i>
                            {t('settings.cancelSubscription')}
                          </Button>
                          <div className="mt-2">
                            <small className="text-muted">
                              {t('settings.cancelSubscriptionHelp')}
                            </small>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {accountTier === 'free' && (
                    <div className="mb-4">
                      <Button
                        variant="success"
                        onClick={() => navigate('/pricing')}
                      >
                        <i className="bi bi-rocket-takeoff me-2"></i>
                        {t('settings.upgradeToPro')}
                      </Button>
                      <div className="mt-2">
                        <small className="text-muted">
                          {t('settings.upgradeToProHelp')}
                        </small>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {accountTier === 'admin' && (
            <Col xs={12} className="mb-4">
              <Card className="shadow-sm h-100">
                <Card.Body className="p-4">
                  <h4 className="mb-4">
                    <i className="bi bi-graph-up me-2"></i>
                    {t('settings.aiTokenUsage')}
                  </h4>
                  
                  {loadingTokenUsage ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">{t('settings.loadingUsageData')}</span>
                    </div>
                  ) : tokenUsage ? (
                    <>
                      <div className="mb-4">
                        <Row>
                          <Col md={3} className="mb-3">
                            <div className="text-center p-3 bg-light rounded">
                              <div className="h5 mb-1 text-primary">{formatNumber(tokenUsage.totalTokens)}</div>
                              <small className="text-muted">{t('settings.totalTokens')}</small>
                            </div>
                          </Col>
                          <Col md={3} className="mb-3">
                            <div className="text-center p-3 bg-light rounded">
                              <div className="h5 mb-1 text-info">{formatNumber(tokenUsage.totalPromptTokens)}</div>
                              <small className="text-muted">{t('settings.promptTokens')}</small>
                            </div>
                          </Col>
                          <Col md={3} className="mb-3">
                            <div className="text-center p-3 bg-light rounded">
                              <div className="h5 mb-1 text-success">{formatNumber(tokenUsage.totalCompletionTokens)}</div>
                              <small className="text-muted">{t('settings.completionTokens')}</small>
                            </div>
                          </Col>
                          <Col md={3} className="mb-3">
                            <div className="text-center p-3 bg-light rounded">
                              <div className="h5 mb-1 text-warning">{formatNumber(tokenUsage.totalRequests)}</div>
                              <small className="text-muted">{t('settings.totalRequests')}</small>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {Object.keys(tokenUsage.byProvider || {}).length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-3">{t('settings.byProvider')}</h6>
                          <Row>
                            {Object.entries(tokenUsage.byProvider).map(([provider, data]) => (
                              <Col md={4} key={provider} className="mb-3">
                                <Card className="border">
                                  <Card.Body className="p-3">
                                    <div className="fw-bold mb-2">{provider}</div>
                                    <div className="small">
                                      <div>{t('settings.total')}: <strong>{formatNumber(data.totalTokens)}</strong></div>
                                      <div className="text-muted">
                                        {t('settings.prompt')}: {formatNumber(data.promptTokens)} | 
                                        {t('settings.completion')}: {formatNumber(data.completionTokens)}
                                      </div>
                                      <div className="text-muted mt-1">
                                        {t('settings.requests')}: {formatNumber(data.requests)}
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}

                      {Object.keys(tokenUsage.byFeature || {}).length > 0 && (
                        <div>
                          <h6 className="mb-3">{t('settings.byFeature')}</h6>
                          <Row>
                            {Object.entries(tokenUsage.byFeature).map(([feature, data]) => (
                              <Col md={4} key={feature} className="mb-3">
                                <Card className="border">
                                  <Card.Body className="p-3">
                                    <div className="fw-bold mb-2 text-capitalize">
                                      {feature.replace(/_/g, ' ')}
                                    </div>
                                    <div className="small">
                                      <div>{t('settings.total')}: <strong>{formatNumber(data.totalTokens)}</strong></div>
                                      <div className="text-muted">
                                        {t('settings.prompt')}: {formatNumber(data.promptTokens)} | 
                                        {t('settings.completion')}: {formatNumber(data.completionTokens)}
                                      </div>
                                      <div className="text-muted mt-1">
                                        {t('settings.requests')}: {formatNumber(data.requests)}
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}

                      {tokenUsage.totalTokens === 0 && (
                        <div className="text-center text-muted py-3">
                          <i className="bi bi-info-circle me-2"></i>
                          {t('settings.noTokenUsage')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-3">
                      <i className="bi bi-exclamation-circle me-2"></i>
                      {t('settings.unableToLoadUsage')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            )}

            <Col xs={12} className="mb-4">
              <Card className="shadow-sm">
                <Card.Body className="p-4">
                  <h4 className="mb-2">{t('settings.brandImages')}</h4>
                  <p className="form-text mb-3">{t('settings.brandImagesHelp')}</p>
                  {imagesList.map((img, index) => (
                    <div key={index} className="mb-3 p-3 border rounded bg-light">
                      <Row className="g-2 align-items-center">
                        <Col xs={12} md={4}>
                          <Form.Control
                            type="url"
                            placeholder={t('settings.imageUrl')}
                            value={img.url || ''}
                            onChange={(e) => handleUpdateImageLink(index, 'url', e.target.value)}
                            size="sm"
                          />
                        </Col>
                        <Col xs={12} md={3}>
                          <Form.Control
                            type="text"
                            placeholder={t('settings.imageName')}
                            value={img.name || ''}
                            onChange={(e) => handleUpdateImageLink(index, 'name', e.target.value)}
                            size="sm"
                          />
                        </Col>
                        <Col xs={12} md={3}>
                          <Form.Select
                            size="sm"
                            value={img.type || 'icon'}
                            onChange={(e) => handleUpdateImageLink(index, 'type', e.target.value)}
                          >
                            <option value="icon">{t('settings.imageTypeIcon')}</option>
                            <option value="banner">{t('settings.imageTypeBanner')}</option>
                          </Form.Select>
                        </Col>
                        <Col xs={12} md={2}>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveImageLink(index)}
                          >
                            {t('settings.remove')}
                          </Button>
                        </Col>
                      </Row>
                      {img.url && (
                        <div className="mt-2">
                          <img src={img.url} alt={img.name || ''} style={{ maxHeight: 32, maxWidth: 64, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                    </div>
                  ))}
                  <Button variant="outline-primary" size="sm" onClick={handleAddImageLink} className="me-2">
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('settings.addImageLink')}
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSaveImages} disabled={savingImages}>
                    {savingImages ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                    {t('settings.saveImages')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12}>
              <Card className="shadow-sm border-danger">
                <Card.Body className="p-4">
                  <h5 className="text-danger mb-3">{t('settings.dangerZone')}</h5>
                  <p className="text-muted mb-3">
                    {t('settings.dangerZoneText')}
                  </p>
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <i className="bi bi-trash me-2"></i>
                    {t('settings.deleteAccount')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

      </Container>

      {/* Cancel Subscription Modal */}
      <Modal 
        show={showCancelModal} 
        onHide={() => !isCancelling && setShowCancelModal(false)} 
        centered
      >
        <Modal.Header closeButton={!isCancelling}>
          <Modal.Title>{t('settings.cancelSubscriptionTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <strong>{t('settings.cancelSubscriptionConfirm')}</strong>
          </p>
          <p className="mb-3">
            {t('settings.cancelSubscriptionText')} {subscriptionEndDate ? formatDateWithLocale(subscriptionEndDate, 'PPP') : t('settings.endOfBillingPeriod')}{t('settings.cancelSubscriptionTextEnd')}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCancelModal(false);
            }}
            disabled={isCancelling}
          >
            {t('settings.keepSubscription')}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleCancelSubscription()}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('settings.cancelling')}
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-2"></i>
                {t('settings.cancelSubscription')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => !isDeleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>{t('settings.deleteAccountTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <strong>{t('settings.deleteAccountConfirm')}</strong>
          </p>
          <p className="mb-3">
            {t('settings.deleteAccountText')}
          </p>
          <ul>
            <li>{t('settings.deleteAccountList1')}</li>
            <li>{t('settings.deleteAccountList2')}</li>
            <li>{t('settings.deleteAccountList3')}</li>
            <li>{t('settings.deleteAccountList4')}</li>
          </ul>
          <p className="text-danger mt-3 mb-2">
            <strong>{t('settings.deleteAccountTypeConfirm')}</strong>
          </p>
          <input
            type="text"
            className="form-control"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={t('settings.deleteAccountPlaceholder')}
            disabled={isDeleting}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={isDeleting || deleteConfirmText !== 'DELETE'}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('settings.deleting')}
              </>
            ) : (
              t('settings.deleteAccount')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <VersionFooter />
    </>
  );
}

